import React, { useState, useEffect } from 'react';
import { Queue } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
import { generateBreadcrumbPath } from '../components/common/breadcrumbs';
import { toast } from 'react-toastify';
import { 
  IconButton, 
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  TextField,
  Drawer,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { Refresh, Visibility } from '@mui/icons-material';
import { TaskLogAccordion } from '../components/common/ExecutionTraceDrawer';
import { fetchTaskExecutionLogs } from '../services/executionTraceApi';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/agent/task-queue`;
const REFRESH_INTERVAL_MS = 10000;

const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');

const AgentTaskQueue = () => {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [agentFilter, setAgentFilter] = useState('ALL');
  const [agentOptions, setAgentOptions] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionLogsLoading, setExecutionLogsLoading] = useState(false);
  const [executionLogsError, setExecutionLogsError] = useState(null);

  useEffect(() => {
    fetchAgentNames();
    fetchTasks();
  }, [statusFilter, agentFilter]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchTasks(true);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [statusFilter, agentFilter]);

  const fetchAgentNames = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/agent/state/tenant/${tenantId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const states = await response.json();
        const options = states
          .map((state) => {
            const value =
              state.agentId ||
              state.tenantAgentInstanceId ||
              state.agentUniqueId;
            const label = state.agentName || state.agentDisplayName;
            if (!value || !label) return null;
            return { value: String(value), label: String(label) };
          })
          .filter(Boolean);
        setAgentOptions((prev) => {
          const merged = new Map(prev.map((opt) => [opt.value, opt.label]));
          options.forEach((opt) => {
            merged.set(opt.value, opt.label);
          });
          return Array.from(merged.entries()).map(([value, label]) => ({ value, label }));
        });
      }
    } catch (error) {
      console.error('Error fetching agent names:', error);
    }
  };

  const fetchTasks = async (silent = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    try {
      let allTasks = [];
      let fetchSucceeded = false;

      if (statusFilter === 'ALL') {
        const response = await fetch(
          `${API_BASE_URL}/tenant/${tenantId}/all`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          allTasks = agentFilter === 'ALL'
            ? data
            : data.filter(task => task.agentId === agentFilter);
          fetchSucceeded = true;
        }
      } else {
        const response = await fetch(
          `${API_BASE_URL}/tenant/${tenantId}/status/${statusFilter}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          allTasks = agentFilter === 'ALL'
            ? data
            : data.filter(task => task.agentId === agentFilter);
          fetchSucceeded = true;
        }
      }

      if (!fetchSucceeded) {
        if (!silent) {
          setTasks([]);
        }
        return;
      }
      
      // Extract agent options from fetched tasks so filter shows display names, not IDs.
      const taskAgentOptions = allTasks
        .map((task) => {
          const value = task.agentId ? String(task.agentId) : null;
          const displayName = task.agentDisplayName || task.agentName;
          if (!value || !displayName) return null;
          const label = String(displayName);
          return { value, label };
        })
        .filter(Boolean);
      if (taskAgentOptions.length > 0) {
        setAgentOptions((prev) => {
          const merged = new Map(prev.map((opt) => [opt.value, opt.label]));
          taskAgentOptions.forEach((opt) => {
            merged.set(opt.value, opt.label);
          });
          return Array.from(merged.entries()).map(([value, label]) => ({ value, label }));
        });
      }

      // Remove duplicates and sort by scheduled_for descending
      const uniqueTasks = Array.from(
        new Map(allTasks.map(task => [task.taskId, task])).values()
      ).sort((a, b) => {
        const dateA = new Date(a.scheduledFor || a.createdAt);
        const dateB = new Date(b.scheduledFor || b.createdAt);
        return dateB - dateA;
      });

      const mappedData = uniqueTasks.map((item) => ({
        id: item.taskId,
        taskId: item.taskId,
        agentId: item.agentId,
        agentDisplayName: item.agentDisplayName ?? item.agentId,
        taskName: item.taskName ?? '—',
        taskType: item.taskType,
        scheduledFor: item.scheduledFor,
        status: item.status,
        resultSummary: item.resultSummary || 'N/A',
        taskKey: item.taskKey,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        runAction: item.runAction,
      }));

      setTasks(mappedData);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      if (!silent) {
        toast.error(`Error loading tasks: ${error.message}`);
        setTasks([]);
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const handleDelete = async (taskId) => {
    if (!taskId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `HTTP ${response.status}`);
      }
      toast.success('Task deleted successfully');
      await fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(`Failed to delete task: ${error.message}`);
      throw error;
    }
  };

  const handleViewDetails = async (taskId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${taskId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSelectedTask(data);
      setDrawerOpen(true);
      setExecutionLogs([]);
      setExecutionLogsError(null);
      setExecutionLogsLoading(true);
      try {
        const logs = await fetchTaskExecutionLogs(taskId);
        setExecutionLogs(Array.isArray(logs) ? logs : []);
      } catch (logErr) {
        console.error('Error fetching execution logs:', logErr);
        setExecutionLogsError(logErr.message || 'Failed to load execution log');
        setExecutionLogs([]);
      } finally {
        setExecutionLogsLoading(false);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error(`Error loading task details: ${error.message}`);
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    try {
      const date = new Date(dateTime);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'default';
      case 'RUNNING':
        return 'info';
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'BLOCKED':
        return 'warning';
      case 'CANCELLED':
        return 'default';
      default:
        return 'default';
    }
  };

  const renderSimpleMarkdown = (text) => {
    if (!text) return '';
    const parts = String(text).split(/(\*\*.+?\*\*)/g);
    return parts.map((part, index) => {
      const isBold = part.startsWith('**') && part.endsWith('**') && part.length > 4;
      if (!isBold) return <React.Fragment key={index}>{part}</React.Fragment>;
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    });
  };

  const columns = [
    { field: 'taskName', headerName: 'Task Name', width: 200, flex: 1 },
    { field: 'agentDisplayName', headerName: 'Agent', width: 180 },
    { field: 'taskType', headerName: 'Task Type', width: 180 },
    { 
      field: 'scheduledFor', 
      headerName: 'Scheduled For', 
      width: 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    { 
      field: 'resultSummary', 
      headerName: 'Result Summary', 
      width: 250,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '100%',
        }}>
          {renderSimpleMarkdown(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Details">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(params.row.taskId);
            }}
            color="primary"
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const breadcrumbPaths = generateBreadcrumbPath('Agent Task Queue');

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="ALL">All Statuses</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="RUNNING">Running</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
            <MenuItem value="BLOCKED">Blocked</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Agent</InputLabel>
          <Select
            value={agentFilter}
            label="Agent"
            onChange={(e) => setAgentFilter(e.target.value)}
          >
            <MenuItem value="ALL">All Agents</MenuItem>
            {agentOptions.map((agent) => (
              <MenuItem key={agent.value} value={agent.value}>
                {agent.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Refresh">
          <IconButton onClick={() => fetchTasks()} color="default" sx={{ ml: 'auto' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <DynamicContainer
        title="Agent Task Queue"
        icon={Queue}
        columns={columns}
        formFields={[]}
        initialFormData={{}}
        idField="taskId"
        breadcrumbPaths={breadcrumbPaths}
        rows={tasks}
        onCreate={null}
        onUpdate={null}
        onDelete={handleDelete}
        isLoading={isLoading}
        hideAdd={true}
        hideEdit={true}
      />

      {/* Task Details Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedTask(null);
          setExecutionLogs([]);
          setExecutionLogsError(null);
        }}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 640 } }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Task Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {selectedTask && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Task ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {selectedTask.taskId}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Agent ID
                </Typography>
                <Typography variant="body1">
                  {selectedTask.agentId}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Task Type
                </Typography>
                <Typography variant="body1">
                  {selectedTask.taskType}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedTask.status}
                  color={getStatusColor(selectedTask.status)}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Scheduled For
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedTask.scheduledFor)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedTask.createdAt)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedTask.updatedAt)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Task Key
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', wordBreak: 'break-all' }}>
                  {selectedTask.taskKey || 'N/A'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Result Summary
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1.5,
                    minHeight: '90px',
                    maxHeight: '220px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    backgroundColor: '#fff',
                  }}
                >
                  {renderSimpleMarkdown(selectedTask.resultSummary || '')}
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Run Action (JSON)"
                value={selectedTask.runAction ? JSON.stringify(selectedTask.runAction, null, 2) : ''}
                disabled
                multiline
                rows={8}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                Execution log
              </Typography>

              {executionLogsLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Loading execution log…
                </Typography>
              )}

              {executionLogsError && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {executionLogsError}
                </Typography>
              )}

              {!executionLogsLoading && !executionLogsError && executionLogs.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No execution log recorded for this task yet.
                </Typography>
              )}

              {!executionLogsLoading &&
                executionLogs.map((log, index) => (
                  <TaskLogAccordion key={log.executionId || index} log={log} index={index} />
                ))}

              <Button
                variant="outlined"
                onClick={() => setDrawerOpen(false)}
                fullWidth
              >
                Close
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default AgentTaskQueue;
